import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildClassicSidebarIframeUrl,
  buildInitialValues,
  collectFieldNames,
  escapeAttribute,
  findFirstErrorFieldName,
  findStepIndexForField,
  isRenderableSchemaPayload,
  normalizeMessages,
  readApiPayload,
  validateFields,
} from './utils.js';
import { FieldRenderer, MessageList } from './fields.jsx';

export function ClassicSidebarFallback({ url }) {
  if (!url) {
    return <div className="cat-react-state cat-react-state--error">Schema not available.</div>;
  }

  const iframeUrl = buildClassicSidebarIframeUrl(url);

  return (
    <div className="cat-react-sidebar-fallback" data-cst-sidebar-fallback-shell="1">
      <iframe
        className="cat-react-sidebar-fallback__frame"
        data-cst-sidebar-modal-source="1"
        src={iframeUrl}
        title="Classic Drupal form"
      />
    </div>
  );
}

export function App({ settings }) {
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fatalError, setFatalError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [pendingFocusFieldName, setPendingFocusFieldName] = useState('');
  const [sidePanelFallbackUrl, setSidePanelFallbackUrl] = useState('');
  const shellRef = useRef(null);

  useEffect(() => {
    let isActive = true;

    async function loadSchema() {
      console.log('[cst_admin_tools] loading schema', settings.schemaUrl);
      setLoading(true);
      setFatalError('');
      setSidePanelFallbackUrl('');

      if (settings.layout === 'sidebar' && !settings.schemaUrl && settings.fallbackUrl) {
        setSidePanelFallbackUrl(settings.fallbackUrl);
        setLoading(false);
        return;
      }

      try {
        const response = await window.fetch(settings.schemaUrl, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        const data = await readApiPayload(response);
        if (!response.ok) {
          throw new Error(data.message || `Unable to load schema (${response.status})`);
        }

        if (!isActive) {
          return;
        }

        console.log('[cst_admin_tools] schema loaded', data);

        if (!isRenderableSchemaPayload(data)) {
          if (settings.layout === 'sidebar' && settings.fallbackUrl) {
            setSidePanelFallbackUrl(settings.fallbackUrl);
            return;
          }

          throw new Error(data.message || 'Schema not available.');
        }

        if (settings.layout === 'sidebar' && data.mode === 'detail' && (settings.fallbackUrl || data.fallbackUrl)) {
          setSidePanelFallbackUrl(settings.fallbackUrl || data.fallbackUrl);
          return;
        }

        if (settings.layout === 'sidebar' && data.reactSupported === false && (settings.fallbackUrl || data.fallbackUrl)) {
          setSidePanelFallbackUrl(settings.fallbackUrl || data.fallbackUrl);
          return;
        }

        setSchema(data);
        setValues(buildInitialValues(data.sections));
        setErrors({});
        setMessages(normalizeMessages(data.messages));
        setCurrentStep(0);
      } catch (error) {
        if (isActive) {
          console.error('[cst_admin_tools] schema load failed', error);
          if (settings.layout === 'sidebar' && settings.fallbackUrl) {
            setSidePanelFallbackUrl(settings.fallbackUrl);
            setFatalError('');
            return;
          }
          setFatalError(error.message || 'Unable to load schema.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }

    loadSchema();
    return () => {
      isActive = false;
    };
  }, [settings.fallbackUrl, settings.layout, settings.schemaUrl]);

  useEffect(() => {
    if (!pendingFocusFieldName || !shellRef.current) {
      return;
    }

    const selector = `[name="${escapeAttribute(pendingFocusFieldName)}"]`;
    const fieldElement = shellRef.current.querySelector(selector);
    if (fieldElement instanceof HTMLElement) {
      fieldElement.focus();
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setPendingFocusFieldName('');
  }, [pendingFocusFieldName, currentStep]);

  const currentSection = useMemo(() => schema?.sections?.[currentStep] ?? null, [schema, currentStep]);
  const hasMultipleSteps = (schema?.sections?.length || 0) > 1;

  function handleChange(name, nextValue) {
    setValues((previous) => ({
      ...previous,
      [name]: nextValue,
    }));
    setErrors((previous) => {
      const nextErrors = { ...previous };
      delete nextErrors[name];
      return nextErrors;
    });
    setMessages([]);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!schema?.canSubmit || !schema?.submitUrl || settings.layout === 'sidebar') {
      return;
    }

    const clientErrors = validateFields(schema.sections, values);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      const firstErrorFieldName = findFirstErrorFieldName(clientErrors, schema.sections);
      const nextStepIndex = findStepIndexForField(firstErrorFieldName, schema.sections);
      if (nextStepIndex !== null) setCurrentStep(nextStepIndex);
      if (firstErrorFieldName) setPendingFocusFieldName(firstErrorFieldName);
      return;
    }

    setSubmitting(true);
    setErrors({});
    setMessages([]);

    try {
      const response = await window.fetch(schema.submitUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await readApiPayload(response);
      if (!response.ok || !data.ok) {
        const nextErrors = data.errors || {};
        const nextMessages = normalizeMessages(
          data.messages,
          data.message || 'Unable to save this form.',
        );
        const firstErrorFieldName = findFirstErrorFieldName(nextErrors, schema.sections);
        const nextStepIndex = findStepIndexForField(firstErrorFieldName, schema.sections);

        setErrors(nextErrors);
        setMessages(nextMessages);

        if (nextStepIndex !== null) {
          setCurrentStep(nextStepIndex);
        }

        if (firstErrorFieldName) {
          setPendingFocusFieldName(firstErrorFieldName);
        }

        return;
      }

      window.location.assign(data.redirectUrl || schema.fallbackUrl);
    } catch (error) {
      setMessages(normalizeMessages([], error.message || 'Unable to save this form.'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="cat-react-state">Loading schema...</div>;
  }

  if (sidePanelFallbackUrl) {
    return <ClassicSidebarFallback url={sidePanelFallbackUrl} />;
  }

  if (fatalError && !schema) {
    return <div className="cat-react-state cat-react-state--error">{fatalError}</div>;
  }

  if (!schema) {
    return <div className="cat-react-state cat-react-state--error">Schema not available.</div>;
  }

  const shellClassName = `cat-react-shell${settings.layout === 'sidebar' ? ' cat-react-shell--sidebar' : ''}`;
  const title = schema.mode === 'detail'
    ? 'Reactive detail view'
    : schema.mode === 'edit'
      ? 'Reactive edit form'
      : 'Reactive create form';
  const showPageHeader = settings.layout !== 'sidebar';
  const isReadOnly = settings.layout === 'sidebar' || schema.mode === 'detail';
  const showUnsupportedBanner = schema.reactSupported === false && !isReadOnly;

  return (
    <div ref={shellRef} className={shellClassName}>
      {showPageHeader ? (
        <header className="cat-react-shell__header">
          <div>
            <p className="cat-react-shell__eyebrow">Drupal React CRUD</p>
            <h1 className="cat-react-shell__title">{title}</h1>
          </div>
          <a className="cat-react-shell__link" href={schema.fallbackUrl}>
            Open classic Drupal form
          </a>
        </header>
      ) : null}

      <MessageList messages={messages} />

      {showUnsupportedBanner ? (
        <div className="cat-react-banner">
          This form still contains fields not supported by the React renderer. Use the classic Drupal form for now.
        </div>
      ) : null}

      {hasMultipleSteps ? (
        <nav className="cat-react-steps" aria-label="Form steps">
          {schema.sections.map((section, index) => {
            const fieldNames = [];
            (section.fields || []).forEach((field) => collectFieldNames(field, fieldNames));
            const hasStepError = fieldNames.some((fieldName) => errors[fieldName]);

            return (
              <button
                key={section.key}
                className={`cat-react-step ${index === currentStep ? 'is-active' : ''}${hasStepError ? ' has-error' : ''}`}
                type="button"
                onClick={() => setCurrentStep(index)}
              >
                {section.title}
              </button>
            );
          })}
        </nav>
      ) : null}

      <form className="cat-react-form" onSubmit={handleSubmit} noValidate>
        {currentSection ? (
          <section className="cat-react-section">
            {hasMultipleSteps ? <h2 className="cat-react-section__title">{currentSection.title}</h2> : null}
            <div className="cat-react-grid">
              {currentSection.fields.map((field) => (
                <FieldRenderer
                  key={field.name}
                  field={field}
                  values={values}
                  errors={errors}
                  disabled={!schema.canSubmit || isReadOnly}
                  onChange={handleChange}
                />
              ))}
            </div>
          </section>
        ) : null}

        <footer className="cat-react-actions">
          {hasMultipleSteps ? (
            <button
              type="button"
              className="cat-react-button cat-react-button--secondary"
              onClick={() => setCurrentStep((previous) => Math.max(0, previous - 1))}
              disabled={currentStep === 0}
            >
              Previous
            </button>
          ) : null}
          {hasMultipleSteps && currentStep < schema.sections.length - 1 ? (
            <button
              type="button"
              className="cat-react-button"
              onClick={() => setCurrentStep((previous) => Math.min(schema.sections.length - 1, previous + 1))}
            >
              Next
            </button>
          ) : !isReadOnly ? (
            <button className="cat-react-button" type="submit" disabled={!schema.canSubmit || submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          ) : null}
        </footer>
      </form>
    </div>
  );
}
